import { useEffect, useMemo, useRef, useState } from "react";
import {
    Type, QrCode, Barcode, Image as ImageIcon, Minus, Square as SquareIcon,
    Trash2, Copy as CopyIcon, ZoomIn, ZoomOut, Undo2, Redo2,
    Save, FolderOpen, Printer, FileText, Sun, Moon, Bluetooth, Usb, X,
    AlignLeft, AlignCenter, AlignRight, Bold,
} from "lucide-react";
import { api, downloadAuthed, API, getAuthToken } from "@/lib/api";

const MM_TO_PX = 4; // base preview scale at zoom 1

const newId = () => `e_${Math.random().toString(36).slice(2, 9)}`;

const blankElement = (type) => {
    const common = { id: newId(), type, x: 2, y: 2, width: 20, height: 8, font_size: 10, bold: false, italic: false, align: "left", rotate: 0, stroke_width: 0.3 };
    if (type === "text") return { ...common, text: "Text" };
    if (type === "barcode") return { ...common, width: 32, height: 10, text: "{{part_number}}", barcode_format: "CODE128", barcode_show_text: true };
    if (type === "qr") return { ...common, width: 18, height: 18, text: "{{id}}", barcode_format: "QR" };
    if (type === "image") return { ...common, width: 20, height: 20, image_url: "" };
    if (type === "line") return { ...common, width: 30, height: 0.4, stroke_width: 0.4 };
    if (type === "rect") return { ...common, width: 30, height: 15, stroke_width: 0.3 };
    return common;
};

const PLACEHOLDER_TOKENS = [
    "{{part_number}}", "{{name}}", "{{category}}", "{{stock_qty}}",
    "{{price}}", "{{id}}", "{{serial}}", "{{date}}", "{{time}}", "{{datetime}}",
];

const BLANK_TEMPLATE = {
    name: "New label",
    width_mm: 50,
    height_mm: 30,
    margin_mm: 1.5,
    gap_mm: 2,
    print_density: 8,
    print_speed: 4,
    default_copies: 1,
    dark_mode: false,
    invert_bitmap: true,
    protocol: "escpos",
    elements: [],
    is_preset: false,
};

export default function LabelEditor({ open, onClose, initialProductIds = [] }) {
    const [templates, setTemplates] = useState([]);
    const [doc, setDoc] = useState(BLANK_TEMPLATE);
    const [docId, setDocId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [zoom, setZoom] = useState(4);
    const [productIds, setProductIds] = useState(initialProductIds);
    const [copies, setCopies] = useState(1);
    const [printing, setPrinting] = useState(false);
    const [bluetoothDevice, setBluetoothDevice] = useState(null);
    const [history, setHistory] = useState({ stack: [BLANK_TEMPLATE], pos: 0 });
    const canvasRef = useRef(null);
    const draggingRef = useRef(null);

    // Load templates
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/admin/label-templates");
                setTemplates(data || []);
            } catch {}
        })();
    }, [open]);

    useEffect(() => { setProductIds(initialProductIds); }, [initialProductIds]);

    // Undo/Redo helpers
    const pushHistory = (next) => {
        setHistory((h) => {
            const newStack = h.stack.slice(0, h.pos + 1);
            newStack.push(next);
            if (newStack.length > 60) newStack.shift();
            return { stack: newStack, pos: newStack.length - 1 };
        });
    };
    const updateDoc = (next) => { setDoc(next); pushHistory(next); };
    const undo = () => setHistory((h) => {
        if (h.pos <= 0) return h;
        const np = h.pos - 1;
        setDoc(h.stack[np]);
        return { ...h, pos: np };
    });
    const redo = () => setHistory((h) => {
        if (h.pos >= h.stack.length - 1) return h;
        const np = h.pos + 1;
        setDoc(h.stack[np]);
        return { ...h, pos: np };
    });

    // Keyboard shortcuts
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
                e.preventDefault();
                e.shiftKey ? redo() : undo();
            } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") {
                e.preventDefault();
                duplicateSelected();
            } else if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
                e.preventDefault();
                deleteSelected();
            } else if (selectedId && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
                e.preventDefault();
                const step = e.shiftKey ? 1 : 0.5;
                nudge(e.key, step);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedId, doc]);

    const selected = doc.elements.find((e) => e.id === selectedId) || null;

    const updateElement = (id, patch) => {
        const next = { ...doc, elements: doc.elements.map((e) => (e.id === id ? { ...e, ...patch } : e)) };
        updateDoc(next);
    };

    const addElement = (type) => {
        const el = blankElement(type);
        // Center it
        el.x = Math.max(1, (doc.width_mm - el.width) / 2);
        el.y = Math.max(1, (doc.height_mm - el.height) / 2);
        const next = { ...doc, elements: [...doc.elements, el] };
        updateDoc(next);
        setSelectedId(el.id);
    };

    const deleteSelected = () => {
        if (!selectedId) return;
        updateDoc({ ...doc, elements: doc.elements.filter((e) => e.id !== selectedId) });
        setSelectedId(null);
    };

    const duplicateSelected = () => {
        if (!selectedId) return;
        const src = doc.elements.find((e) => e.id === selectedId);
        if (!src) return;
        const clone = { ...src, id: newId(), x: Math.min(doc.width_mm - src.width - 1, src.x + 2), y: Math.min(doc.height_mm - src.height - 1, src.y + 2) };
        updateDoc({ ...doc, elements: [...doc.elements, clone] });
        setSelectedId(clone.id);
    };

    const nudge = (key, step) => {
        if (!selectedId) return;
        const e = doc.elements.find((x) => x.id === selectedId);
        if (!e) return;
        let { x, y } = e;
        if (key === "ArrowUp") y = Math.max(0, y - step);
        if (key === "ArrowDown") y = Math.min(doc.height_mm - e.height, y + step);
        if (key === "ArrowLeft") x = Math.max(0, x - step);
        if (key === "ArrowRight") x = Math.min(doc.width_mm - e.width, x + step);
        updateElement(e.id, { x, y });
    };

    // Drag/Resize on canvas
    const onElementMouseDown = (e, el, mode = "move") => {
        e.stopPropagation();
        setSelectedId(el.id);
        const rect = canvasRef.current.getBoundingClientRect();
        draggingRef.current = {
            id: el.id, mode,
            startX: e.clientX, startY: e.clientY,
            ox: el.x, oy: el.y, ow: el.width, oh: el.height,
            canvasLeft: rect.left, canvasTop: rect.top,
        };
        document.body.style.cursor = mode === "move" ? "grabbing" : "nwse-resize";
    };

    useEffect(() => {
        const onMove = (e) => {
            const d = draggingRef.current;
            if (!d) return;
            const dxMm = (e.clientX - d.startX) / zoom;
            const dyMm = (e.clientY - d.startY) / zoom;
            const el = doc.elements.find((x) => x.id === d.id);
            if (!el) return;
            if (d.mode === "move") {
                const nx = Math.max(0, Math.min(doc.width_mm - el.width, d.ox + dxMm));
                const ny = Math.max(0, Math.min(doc.height_mm - el.height, d.oy + dyMm));
                setDoc((cur) => ({ ...cur, elements: cur.elements.map((x) => x.id === d.id ? { ...x, x: nx, y: ny } : x) }));
            } else if (d.mode === "resize") {
                const nw = Math.max(2, Math.min(doc.width_mm - el.x, d.ow + dxMm));
                const nh = Math.max(2, Math.min(doc.height_mm - el.y, d.oh + dyMm));
                setDoc((cur) => ({ ...cur, elements: cur.elements.map((x) => x.id === d.id ? { ...x, width: nw, height: nh } : x) }));
            }
        };
        const onUp = () => {
            if (draggingRef.current) {
                draggingRef.current = null;
                document.body.style.cursor = "";
                // Persist undo state after drag
                pushHistory(doc);
            }
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc, zoom]);

    // Save / Load
    const saveTemplate = async () => {
        try {
            const payload = { ...doc };
            delete payload.id; delete payload.created_at; delete payload.updated_at;
            if (docId) {
                const { data } = await api.put(`/admin/label-templates/${docId}`, payload);
                setDocId(data.id);
            } else {
                const { data } = await api.post("/admin/label-templates", payload);
                setDocId(data.id);
            }
            const { data: list } = await api.get("/admin/label-templates");
            setTemplates(list || []);
            alert("Template saved");
        } catch (e) {
            alert("Save failed: " + (e.response?.data?.detail || e.message));
        }
    };

    const loadTemplate = (tpl) => {
        const clean = { ...tpl };
        delete clean.id; delete clean.created_at; delete clean.updated_at;
        setDoc(clean);
        setDocId(tpl.is_preset ? null : tpl.id); // presets always save as new
        setSelectedId(null);
        setHistory({ stack: [clean], pos: 0 });
    };

    const downloadPdf = async () => {
        setPrinting(true);
        try {
            await downloadAuthed("/admin/labels/render-pdf", `${doc.name}.pdf`, null,
                { method: "POST", body: { template: doc, product_ids: productIds, copies_per_product: copies } });
        } catch (e) {
            alert(e.message);
        } finally { setPrinting(false); }
    };

    const printToBrowser = async () => {
        const token = getAuthToken();
        const res = await fetch(`${API}/admin/labels/render-pdf`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ template: doc, product_ids: productIds, copies_per_product: copies }),
        });
        if (!res.ok) { alert("Render failed: " + res.status); return; }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank");
        if (w) setTimeout(() => { try { w.focus(); w.print(); } catch {} }, 800);
    };

    // Web Bluetooth → TSPL stream
    const connectBluetoothPrinter = async () => {
        if (!navigator.bluetooth) {
            alert("Web Bluetooth is not supported in this browser. Use Chrome/Edge on desktop or Android.");
            return;
        }
        try {
            // OCOM OCBP-M810 commonly exposes Serial-Port-Profile (SPP) via service 0xFFE0 or 0xFF00
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: [0xff00, 0xffe0, 0x18f0, "000018f0-0000-1000-8000-00805f9b34fb"],
            });
            await device.gatt.connect();
            setBluetoothDevice(device);
        } catch (e) {
            alert("Bluetooth pairing failed: " + e.message);
        }
    };

    const getTsplBytes = async () => {
        const { data } = await api.post("/admin/labels/render-tspl", {
            template: doc, product_ids: productIds, copies_per_product: copies,
        });
        const b64 = data.tspl_base64;
        if (!b64) {
            // Legacy text fallback
            return new TextEncoder().encode(data.tspl || "");
        }
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return bytes;
    };

    const printViaBluetooth = async () => {
        if (!bluetoothDevice) { await connectBluetoothPrinter(); if (!bluetoothDevice) return; }
        if (printing) return;  // prevent re-entry while a print is in flight
        setPrinting(true);
        try {
            const bytes = await getTsplBytes();
            const server = await bluetoothDevice.gatt.connect();
            const services = await server.getPrimaryServices();
            // Find a writable characteristic — PREFER the one with `write` (with response)
            // over `writeWithoutResponse` since the printer ACK'ing each chunk prevents
            // buffer overrun that causes garbage on the 2nd+ print.
            let writeChar = null;
            let fallbackChar = null;
            for (const svc of services) {
                const chars = await svc.getCharacteristics();
                for (const ch of chars) {
                    if (ch.properties.write) { writeChar = ch; break; }
                    if (ch.properties.writeWithoutResponse && !fallbackChar) fallbackChar = ch;
                }
                if (writeChar) break;
            }
            writeChar = writeChar || fallbackChar;
            if (!writeChar) { alert("No writable Bluetooth characteristic found on this device."); return; }

            const useAck = !!writeChar.properties.write;
            // OCBP-M810 BLE SPP module: 100-byte chunks + small inter-chunk delay
            // prevents buffer overrun (which manifests as random garbage on subsequent prints).
            const CHUNK = 100;
            const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
            for (let i = 0; i < bytes.length; i += CHUNK) {
                const slice = bytes.slice(i, i + CHUNK);
                if (useAck) {
                    await writeChar.writeValue(slice);
                } else {
                    await writeChar.writeValueWithoutResponse(slice);
                    await sleep(15);  // pace the writes for printers that drop bytes
                }
            }
            // Let the printer finish processing the last bitmap before we allow another print.
            await sleep(800);
            alert("Etiketa u dërgua në printer ✓");
        } catch (e) {
            alert("Bluetooth print failed: " + e.message);
        } finally {
            setPrinting(false);
        }
    };

    // WebUSB → TSPL stream
    const printViaUSB = async () => {
        if (!navigator.usb) { alert("WebUSB is not supported. Use Chrome/Edge on desktop."); return; }
        try {
            const dev = await navigator.usb.requestDevice({ filters: [] });
            try {
                await dev.open();
            } catch (openErr) {
                if (String(openErr.message || "").toLowerCase().includes("access denied")) {
                    alert(
                        "USB AKSES I REFUZUAR\n\n" +
                        "Printerin po e mban driver-i i Windows.\n\n" +
                        "Zgjidhjet:\n" +
                        "  1. PËRDOR BLUETOOTH (rekomanduar — funksionon menjëherë)\n" +
                        "  2. Ose kliko 'PDF' → shkarko → print nga Windows me driverin OCOM\n" +
                        "  3. Ose instalo Zadig dhe zëvendëso driver-in me WinUSB (avancuar)"
                    );
                    return;
                }
                throw openErr;
            }
            if (dev.configuration === null) await dev.selectConfiguration(1);
            const iface = dev.configuration.interfaces[0];
            await dev.claimInterface(iface.interfaceNumber);
            const outEndpoint = iface.alternates[0].endpoints.find((e) => e.direction === "out");
            if (!outEndpoint) { alert("No OUT endpoint on this USB device."); return; }
            const bytes = await getTsplBytes();
            const CHUNK = 4096;
            for (let i = 0; i < bytes.length; i += CHUNK) {
                await dev.transferOut(outEndpoint.endpointNumber, bytes.slice(i, i + CHUNK));
            }
            alert("Sent to USB printer ✓");
            await dev.close();
        } catch (e) {
            if (e.name !== "NotFoundError") {
                alert("USB print failed: " + e.message + "\n\nProvo me Bluetooth — funksionon pa probleme.");
            }
        }
    };

    const dark = !!doc.dark_mode;
    const baseBg = dark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-900";

    if (!open) return null;

    return (
        <div className={`admin-light fixed inset-0 z-50 ${baseBg} flex flex-col`} data-testid="label-editor">
            {/* Top bar */}
            <div className={`${dark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"} border-b px-4 py-2 flex items-center gap-2 flex-wrap`}>
                <div className="font-display font-black uppercase text-lg shrink-0">Label Designer</div>
                <input
                    value={doc.name}
                    onChange={(e) => updateDoc({ ...doc, name: e.target.value })}
                    className={`input-field !py-1 !w-56 !text-sm ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`}
                    data-testid="label-doc-name"
                />
                <select
                    value={docId || ""}
                    onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) loadTemplate(t); }}
                    className={`input-field !py-1 !w-56 !text-sm ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`}
                    data-testid="label-template-select"
                >
                    <option value="">— Load template —</option>
                    {templates.filter((t) => t.is_preset).length > 0 && (
                        <optgroup label="Presets">
                            {templates.filter((t) => t.is_preset).map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </optgroup>
                    )}
                    {templates.filter((t) => !t.is_preset).length > 0 && (
                        <optgroup label="My templates">
                            {templates.filter((t) => !t.is_preset).map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                        </optgroup>
                    )}
                </select>
                <button onClick={saveTemplate} className="btn-outline !py-1 !text-xs" data-testid="label-save"><Save className="w-3.5 h-3.5" /> Save</button>
                <button onClick={undo} className="btn-outline !py-1 !text-xs" disabled={history.pos <= 0} data-testid="label-undo"><Undo2 className="w-3.5 h-3.5" /></button>
                <button onClick={redo} className="btn-outline !py-1 !text-xs" disabled={history.pos >= history.stack.length - 1} data-testid="label-redo"><Redo2 className="w-3.5 h-3.5" /></button>
                <div className="flex items-center gap-1">
                    <button onClick={() => setZoom((z) => Math.max(1, z - 1))} className="btn-outline !py-1 !text-xs"><ZoomOut className="w-3.5 h-3.5" /></button>
                    <span className="text-[10px] font-mono w-8 text-center">{Math.round(zoom / MM_TO_PX * 100)}%</span>
                    <button onClick={() => setZoom((z) => Math.min(20, z + 1))} className="btn-outline !py-1 !text-xs"><ZoomIn className="w-3.5 h-3.5" /></button>
                </div>
                <button onClick={() => updateDoc({ ...doc, dark_mode: !dark })} className="btn-outline !py-1 !text-xs" data-testid="label-darkmode-toggle">
                    {dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                </button>
                <div className="ml-auto flex items-center gap-2">
                    <button onClick={downloadPdf} disabled={printing} className="btn-outline !py-1 !text-xs" data-testid="label-download-pdf"><FileText className="w-3.5 h-3.5" /> PDF</button>
                    <button onClick={printToBrowser} disabled={printing} className="btn-outline !py-1 !text-xs" data-testid="label-browser-print"><Printer className="w-3.5 h-3.5" /> Print</button>
                    <button onClick={printViaBluetooth} className="btn-outline !py-1 !text-xs" data-testid="label-bluetooth"><Bluetooth className="w-3.5 h-3.5" /> {bluetoothDevice ? "BT ✓" : "Bluetooth"}</button>
                    <button onClick={printViaUSB} className="btn-outline !py-1 !text-xs" data-testid="label-usb"><Usb className="w-3.5 h-3.5" /> USB</button>
                    <button onClick={onClose} className="btn-outline !py-1 !text-xs" data-testid="label-editor-close"><X className="w-3.5 h-3.5" /></button>
                </div>
            </div>

            {/* Main */}
            <div className="flex-1 grid grid-cols-[200px_1fr_320px] overflow-hidden">
                {/* Left palette */}
                <div className={`${dark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"} border-r p-3 overflow-y-auto`}>
                    <div className="label-kicker mb-2 text-slate-500">Add element</div>
                    <div className="grid grid-cols-2 gap-2">
                        <PaletteBtn icon={Type} label="Text" onClick={() => addElement("text")} testid="add-text" />
                        <PaletteBtn icon={Barcode} label="Barcode" onClick={() => addElement("barcode")} testid="add-barcode" />
                        <PaletteBtn icon={QrCode} label="QR" onClick={() => addElement("qr")} testid="add-qr" />
                        <PaletteBtn icon={ImageIcon} label="Image" onClick={() => addElement("image")} testid="add-image" />
                        <PaletteBtn icon={Minus} label="Line" onClick={() => addElement("line")} testid="add-line" />
                        <PaletteBtn icon={SquareIcon} label="Box" onClick={() => addElement("rect")} testid="add-rect" />
                    </div>

                    <div className="label-kicker mt-4 mb-2 text-slate-500">Label size (mm)</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <label className="font-mono text-[10px]">
                            Width
                            <input type="number" min="10" max="72" value={doc.width_mm}
                                onChange={(e) => updateDoc({ ...doc, width_mm: Math.min(72, Math.max(10, parseFloat(e.target.value) || 10)) })}
                                className={`input-field !py-1 !text-xs ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`}
                                data-testid="label-width" />
                        </label>
                        <label className="font-mono text-[10px]">
                            Height
                            <input type="number" min="5" max="300" value={doc.height_mm}
                                onChange={(e) => updateDoc({ ...doc, height_mm: Math.max(5, parseFloat(e.target.value) || 30) })}
                                className={`input-field !py-1 !text-xs ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`}
                                data-testid="label-height" />
                        </label>
                    </div>

                    <div className="label-kicker mt-4 mb-2 text-slate-500">Print settings</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <label className="font-mono text-[10px]">
                            Density (1-15)
                            <input type="number" min="1" max="15" value={doc.print_density}
                                onChange={(e) => updateDoc({ ...doc, print_density: Math.max(1, Math.min(15, parseInt(e.target.value) || 8)) })}
                                className={`input-field !py-1 !text-xs ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`} />
                        </label>
                        <label className="font-mono text-[10px]">
                            Speed (1-10)
                            <input type="number" min="1" max="10" value={doc.print_speed}
                                onChange={(e) => updateDoc({ ...doc, print_speed: Math.max(1, Math.min(10, parseInt(e.target.value) || 4)) })}
                                className={`input-field !py-1 !text-xs ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`} />
                        </label>
                        <label className="font-mono text-[10px]">
                            Copies
                            <input type="number" min="1" max="100" value={copies}
                                onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                                className={`input-field !py-1 !text-xs ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`}
                                data-testid="label-copies" />
                        </label>
                        <label className="font-mono text-[10px]">
                            Gap (mm)
                            <input type="number" step="0.5" min="0" max="10" value={doc.gap_mm ?? 2}
                                onChange={(e) => updateDoc({ ...doc, gap_mm: Math.max(0, Math.min(10, parseFloat(e.target.value) || 0)) })}
                                className={`input-field !py-1 !text-xs ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`}
                                title="Gap between labels on the roll — most pre-cut sticker rolls have 2-3 mm"
                                data-testid="label-gap" />
                        </label>
                    </div>
                    <label className={`mt-2 flex items-center gap-2 text-[10px] font-mono cursor-pointer ${dark ? "text-slate-300" : "text-slate-700"}`}>
                        <input
                            type="checkbox"
                            checked={doc.invert_bitmap !== false}
                            onChange={(e) => updateDoc({ ...doc, invert_bitmap: e.target.checked })}
                            className="accent-[hsl(var(--primary))]"
                            data-testid="label-invert-bitmap"
                        />
                        TSPL2 standard bits (0=black) — uncheck if printout is inverted
                    </label>

                    <div className="label-kicker mt-4 mb-2 text-slate-500">Printer protocol</div>
                    <select
                        value={doc.protocol || "escpos"}
                        onChange={(e) => updateDoc({ ...doc, protocol: e.target.value })}
                        className={`input-field !py-1 !text-xs w-full ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`}
                        data-testid="label-protocol"
                        title="OCBP-M810 supports all three. If the printout is garbage text, try a different one."
                    >
                        <option value="escpos">ESC/POS Raster (Receipt mode)</option>
                        <option value="tspl">TSPL (Label mode)</option>
                        <option value="cpcl">CPCL (Citizen)</option>
                    </select>
                    <div className={`text-[9px] font-mono mt-1 leading-tight ${dark ? "text-slate-400" : "text-slate-500"}`}>
                        OCBP-M810 në APP Mode "Reciept" → përdor <strong>ESC/POS</strong>.<br/>
                        Në APP Mode "Label" → përdor <strong>TSPL</strong>.<br/>
                        Nëse të treja japin byralliqe → printerit i duhet ndryshim modi përmes butonave ose programit OCOM.
                    </div>

                    <div className="label-kicker mt-4 mb-2 text-slate-500">Dynamic tokens</div>
                    <div className="space-y-1">
                        {PLACEHOLDER_TOKENS.map((tok) => (
                            <div
                                key={tok}
                                onClick={() => navigator.clipboard?.writeText(tok)}
                                className={`text-[10px] font-mono px-2 py-1 cursor-pointer truncate ${dark ? "bg-slate-700 hover:bg-slate-600" : "bg-white border border-slate-200 hover:border-slate-900"}`}
                                title="Click to copy"
                            >
                                {tok}
                            </div>
                        ))}
                    </div>

                    {productIds.length > 0 && (
                        <div className={`${dark ? "bg-slate-700" : "bg-emerald-50"} mt-4 p-2 text-[10px] font-mono`}>
                            <strong>{productIds.length}</strong> product{productIds.length !== 1 ? "s" : ""} bound
                        </div>
                    )}
                </div>

                {/* Center canvas */}
                <div className={`${dark ? "bg-slate-900" : "bg-slate-100"} overflow-auto p-6 flex items-start justify-center`}>
                    <div
                        ref={canvasRef}
                        onClick={() => setSelectedId(null)}
                        className={`relative shadow-lg ${dark ? "bg-slate-800" : "bg-white"}`}
                        style={{
                            width: doc.width_mm * zoom,
                            height: doc.height_mm * zoom,
                            outline: dark ? "1px solid #475569" : "1px solid #cbd5e1",
                        }}
                        data-testid="label-canvas"
                    >
                        {doc.elements.map((el) => (
                            <ElementBox
                                key={el.id}
                                el={el}
                                zoom={zoom}
                                dark={dark}
                                selected={selectedId === el.id}
                                onMouseDown={onElementMouseDown}
                            />
                        ))}
                    </div>
                </div>

                {/* Right properties */}
                <div className={`${dark ? "border-slate-700 bg-slate-800" : "border-slate-200 bg-slate-50"} border-l p-3 overflow-y-auto`}>
                    {!selected ? (
                        <div className="text-center text-xs font-mono text-slate-500 mt-10">
                            Click an element to edit
                        </div>
                    ) : (
                        <PropertiesPanel
                            el={selected}
                            dark={dark}
                            onChange={(patch) => updateElement(selected.id, patch)}
                            onDelete={deleteSelected}
                            onDuplicate={duplicateSelected}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function PaletteBtn({ icon: Icon, label, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            className="flex flex-col items-center gap-1 p-2 bg-white border border-slate-200 hover:border-[hsl(var(--primary))] hover:bg-slate-50 transition-colors text-slate-900"
            data-testid={testid}
        >
            <Icon className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
        </button>
    );
}

function ElementBox({ el, zoom, dark, selected, onMouseDown }) {
    const style = {
        position: "absolute",
        left: el.x * zoom,
        top: el.y * zoom,
        width: el.width * zoom,
        height: el.height * zoom,
        outline: selected ? "2px solid hsl(var(--primary))" : (dark ? "1px dashed #475569" : "1px dashed #cbd5e1"),
        cursor: "grab",
        userSelect: "none",
        overflow: "hidden",
        background: el.type === "rect" ? "transparent" : (dark ? "rgba(15,23,42,0.4)" : "rgba(241,245,249,0.4)"),
    };
    const renderInner = () => {
        if (el.type === "text") {
            return (
                <div
                    style={{
                        width: "100%", height: "100%",
                        display: "flex", alignItems: "center",
                        justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start",
                        fontSize: `${el.font_size * zoom * 0.32}px`,
                        fontWeight: el.bold ? 800 : 400,
                        fontStyle: el.italic ? "italic" : "normal",
                        padding: "0 2px",
                        whiteSpace: "nowrap",
                    }}
                >
                    {el.text || "(empty)"}
                </div>
            );
        }
        if (el.type === "qr") {
            return <div style={{ background: "repeating-conic-gradient(#000 0% 25%, #fff 0% 50%) 50% / 6px 6px", width: "100%", height: "100%" }} />;
        }
        if (el.type === "barcode") {
            return <div style={{ background: "repeating-linear-gradient(90deg, #000 0 2px, #fff 2px 4px)", width: "100%", height: "100%" }} />;
        }
        if (el.type === "image") {
            return el.image_url
                ? <img src={el.image_url} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none" }} />
                : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontSize: 10, color: "#94a3b8" }}>IMG</div>;
        }
        if (el.type === "line") {
            return <div style={{ background: dark ? "#cbd5e1" : "#0f172a", width: "100%", height: Math.max(1, el.stroke_width * zoom) }} />;
        }
        if (el.type === "rect") {
            return <div style={{ width: "100%", height: "100%", border: `${Math.max(1, el.stroke_width * zoom)}px solid ${dark ? "#cbd5e1" : "#0f172a"}` }} />;
        }
        return null;
    };
    return (
        <div style={style} onMouseDown={(e) => onMouseDown(e, el, "move")} data-testid={`label-el-${el.id}`}>
            {renderInner()}
            {selected && (
                <div
                    style={{ position: "absolute", right: -4, bottom: -4, width: 10, height: 10, background: "hsl(var(--primary))", cursor: "nwse-resize" }}
                    onMouseDown={(e) => onMouseDown(e, el, "resize")}
                    data-testid={`label-el-${el.id}-resize`}
                />
            )}
        </div>
    );
}

function PropertiesPanel({ el, dark, onChange, onDelete, onDuplicate }) {
    const inputCls = `input-field !py-1 !text-xs ${dark ? "!bg-slate-900 !text-slate-100 !border-slate-700" : ""}`;
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="label-kicker text-slate-500">{el.type.toUpperCase()}</div>
                <div className="flex items-center gap-1">
                    <button onClick={onDuplicate} className="btn-outline !py-1 !px-2 !text-xs" title="Duplicate (Ctrl+D)" data-testid="prop-duplicate"><CopyIcon className="w-3 h-3" /></button>
                    <button onClick={onDelete} className="btn-outline !py-1 !px-2 !text-xs !border-red-200 !text-red-600" title="Delete (Del)" data-testid="prop-delete"><Trash2 className="w-3 h-3" /></button>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <label className="text-[10px] font-mono">X (mm)
                    <input type="number" step="0.5" value={el.x.toFixed(1)} onChange={(e) => onChange({ x: parseFloat(e.target.value) || 0 })} className={inputCls} />
                </label>
                <label className="text-[10px] font-mono">Y (mm)
                    <input type="number" step="0.5" value={el.y.toFixed(1)} onChange={(e) => onChange({ y: parseFloat(e.target.value) || 0 })} className={inputCls} />
                </label>
                <label className="text-[10px] font-mono">W (mm)
                    <input type="number" step="0.5" value={el.width.toFixed(1)} onChange={(e) => onChange({ width: parseFloat(e.target.value) || 1 })} className={inputCls} />
                </label>
                <label className="text-[10px] font-mono">H (mm)
                    <input type="number" step="0.5" value={el.height.toFixed(1)} onChange={(e) => onChange({ height: parseFloat(e.target.value) || 1 })} className={inputCls} />
                </label>
            </div>

            {(el.type === "text" || el.type === "barcode" || el.type === "qr") && (
                <label className="block text-[10px] font-mono">{el.type === "text" ? "Text" : "Data"}
                    <textarea rows={2} value={el.text || ""} onChange={(e) => onChange({ text: e.target.value })} className={inputCls} data-testid="prop-text" />
                </label>
            )}

            {el.type === "text" && (
                <>
                    <div className="grid grid-cols-2 gap-2 items-end">
                        <label className="text-[10px] font-mono">Font size
                            <input type="number" min="4" max="48" step="0.5" value={el.font_size} onChange={(e) => onChange({ font_size: parseFloat(e.target.value) || 10 })} className={inputCls} />
                        </label>
                        <button onClick={() => onChange({ bold: !el.bold })} className={`btn-outline !py-1 !text-xs ${el.bold ? "!bg-slate-900 !text-white" : ""}`}><Bold className="w-3 h-3" /> Bold</button>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => onChange({ align: "left" })} className={`flex-1 btn-outline !py-1 !text-xs ${el.align === "left" ? "!bg-slate-900 !text-white" : ""}`}><AlignLeft className="w-3 h-3" /></button>
                        <button onClick={() => onChange({ align: "center" })} className={`flex-1 btn-outline !py-1 !text-xs ${el.align === "center" ? "!bg-slate-900 !text-white" : ""}`}><AlignCenter className="w-3 h-3" /></button>
                        <button onClick={() => onChange({ align: "right" })} className={`flex-1 btn-outline !py-1 !text-xs ${el.align === "right" ? "!bg-slate-900 !text-white" : ""}`}><AlignRight className="w-3 h-3" /></button>
                    </div>
                </>
            )}

            {el.type === "barcode" && (
                <>
                    <label className="block text-[10px] font-mono">Format
                        <select value={el.barcode_format || "CODE128"} onChange={(e) => onChange({ barcode_format: e.target.value })} className={inputCls}>
                            <option value="CODE128">Code 128</option>
                            <option value="CODE39">Code 39</option>
                            <option value="EAN13">EAN-13</option>
                            <option value="UPC">UPC-A</option>
                        </select>
                    </label>
                    <label className="flex items-center gap-2 text-[10px] font-mono">
                        <input type="checkbox" checked={!!el.barcode_show_text} onChange={(e) => onChange({ barcode_show_text: e.target.checked })} />
                        Show readable text
                    </label>
                </>
            )}

            {el.type === "qr" && (
                <label className="block text-[10px] font-mono">Format
                    <select value={el.barcode_format || "QR"} onChange={(e) => onChange({ barcode_format: e.target.value })} className={inputCls}>
                        <option value="QR">QR Code</option>
                        <option value="DATAMATRIX">Data Matrix</option>
                    </select>
                </label>
            )}

            {el.type === "image" && (
                <label className="block text-[10px] font-mono">Image URL
                    <input value={el.image_url || ""} onChange={(e) => onChange({ image_url: e.target.value })} placeholder="https://..." className={inputCls} />
                </label>
            )}

            {(el.type === "line" || el.type === "rect") && (
                <label className="block text-[10px] font-mono">Stroke (mm)
                    <input type="number" step="0.1" min="0.1" max="5" value={el.stroke_width} onChange={(e) => onChange({ stroke_width: parseFloat(e.target.value) || 0.3 })} className={inputCls} />
                </label>
            )}

            <div className="pt-3 border-t border-slate-200 text-[9px] font-mono text-slate-500 space-y-0.5">
                <div>Drag = move · Bottom-right handle = resize</div>
                <div>Arrows = nudge 0.5mm · Shift+Arrow = 1mm</div>
                <div>Ctrl+Z/Shift+Z = undo/redo · Ctrl+D = duplicate</div>
            </div>
        </div>
    );
}
